import { useState, useEffect } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { InputNumber, Divider, message, UploadFile } from 'antd'
import { Input, TextArea, Switch, Select, Checkbox } from '@/components/ui'
import { ImageUploadWithPreview } from '@/components/ui/ImageUpload/ImageUpload'
import {
  PlusCircleFilled,
  MinusCircleOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  CheckCircleOutlined,
  CheckSquareOutlined,
  DownOutlined,
  CloudUploadOutlined,
  CalendarOutlined,
  PlusOutlined,
  PhoneOutlined,
} from '@ant-design/icons'
import { Drawer, Button } from '@/components/ui'
import { api } from '@/lib/apiClient'
import type {
  CountryOption,
  RegistrationField,
  AgendaSession,
  SessionOption,
  FieldOption,
} from '../types'
import {
  DEFAULT_FIRST_NAME_LABEL,
  DEFAULT_LAST_NAME_LABEL,
  firstNameLabel,
  isSplitName,
  lastNameLabel,
} from '../nameField'

interface AddFieldDrawerProps {
  open: boolean
  onClose: () => void
  field: RegistrationField | null
  agendaSessions: AgendaSession[]
  countryOptions: CountryOption[]
  eventSlug: string
  nextPosition: number
  onSaved: (field: RegistrationField) => void
  onDeleted: (id: number) => void
}

const consentEditorModules = {
  toolbar: [['bold', 'italic', 'link']],
}
const consentEditorFormats = ['bold', 'italic', 'link']

// Field type definitions
const FIELD_TYPES = [
  {
    key: 'short_text',
    label: 'Short Text',
    description: 'Answer input field allows up to 200 characters',
    icon: <AlignLeftOutlined />,
  },
  {
    key: 'long_text',
    label: 'Long Text',
    description: 'Answer input field allows up to 300 characters',
    icon: <AlignCenterOutlined />,
  },
  {
    key: 'multiple_choice',
    label: 'Multiple Choice',
    description: 'Multiple checkbox selection up to 60 answers',
    icon: <CheckSquareOutlined />,
  },
  {
    key: 'single_choice',
    label: 'Single Choice',
    description: 'Single selection from multiple options',
    icon: <CheckCircleOutlined />,
  },
  {
    key: 'dropdown',
    label: 'Dropdown',
    description: 'Single selection from dropdown with unlimited answers',
    icon: <DownOutlined />,
  },
  {
    key: 'phone_number',
    label: 'Phone Number',
    description: 'Country code dropdown with a phone number input',
    icon: <PhoneOutlined />,
  },
  {
    key: 'file_upload',
    label: 'File Upload',
    description: 'Supports file upload during form submission',
    icon: <CloudUploadOutlined />,
  },
  {
    key: 'consent_text',
    label: 'Consent Text',
    description: 'A checkbox with up to 20,000 characters statement',
    icon: <CheckCircleOutlined />,
  },
  {
    key: 'session_choice',
    label: 'Session',
    description: 'Dropdown to select sessions',
    icon: <CalendarOutlined />,
  },
]

export default function AddFieldDrawer({
  open,
  onClose,
  field,
  agendaSessions,
  countryOptions,
  eventSlug,
  nextPosition,
  onSaved,
  onDeleted,
}: AddFieldDrawerProps) {
  const [step, setStep] = useState<'select' | 'configure'>('select')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [addImage, setAddImage] = useState(false)
  const [addInstructions, setAddInstructions] = useState(false)
  const [helpText, setHelpText] = useState('')
  const [hintImageFile, setHintImageFile] = useState<File | null>(null)
  const [hintImageList, setHintImageList] = useState<UploadFile[]>([])
  const [existingHintImageUrl, setExistingHintImageUrl] = useState<string | null>(null)
  const [removeHintImage, setRemoveHintImage] = useState(false)
  const [options, setOptions] = useState<FieldOption[]>([{ label: '', value: '', position: 0 }])
  const [removedOptionIds, setRemovedOptionIds] = useState<number[]>([])
  const [sessionOptions, setSessionOptions] = useState<SessionOption[]>([
    { agenda_session_id: 0, capacity: null, position: 0 },
  ])
  const [removedSessionOptionIds, setRemovedSessionOptionIds] = useState<number[]>([])
  const [consentStatement, setConsentStatement] = useState('')
  const [maxLength, setMaxLength] = useState<number>(200)
  const [contentTypes, setContentTypes] = useState<string[]>([
    'image/jpeg',
    'image/png',
    'application/pdf',
  ])
  const [maxSizeMb, setMaxSizeMb] = useState<number>(10)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [bulkAddText, setBulkAddText] = useState('')
  const [pinnedCountries, setPinnedCountries] = useState<string[]>([])
  const [firstNameLabelValue, setFirstNameLabelValue] = useState(DEFAULT_FIRST_NAME_LABEL)
  const [lastNameLabelValue, setLastNameLabelValue] = useState(DEFAULT_LAST_NAME_LABEL)
  const [saving, setSaving] = useState(false)

  const bulkAddLines = bulkAddText
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)

  // Reset form when opening/closing
  useEffect(() => {
    if (open) {
      if (field) {
        // Editing existing field
        setSelectedType(field.kind)
        setLabel(field.label)
        setAddInstructions(!!field.help_text)
        setHelpText(field.help_text || '')
        setExistingHintImageUrl(field.hint_image_url || null)
        setAddImage(!!field.hint_image_url)
        setHintImageFile(null)
        setHintImageList(
          field.hint_image_url
            ? [
                {
                  uid: '-1',
                  name: 'hint image',
                  status: 'done',
                  url: field.hint_image_url,
                },
              ]
            : [],
        )
        setRemoveHintImage(false)
        setOptions(
          field.options.length > 0 ? field.options : [{ label: '', value: '', position: 0 }],
        )
        setRemovedOptionIds([])
        setSessionOptions(
          field.session_options.length > 0
            ? field.session_options
            : [{ agenda_session_id: 0, capacity: null, position: 0 }],
        )
        setRemovedSessionOptionIds([])
        setShowBulkAdd(false)
        setBulkAddText('')
        setConsentStatement(field.configuration?.consent_statement || '')
        setMaxLength(field.configuration?.max_length || 200)
        setContentTypes(
          field.configuration?.content_types || ['image/jpeg', 'image/png', 'application/pdf'],
        )
        setMaxSizeMb(field.configuration?.max_size_mb || 10)
        setPinnedCountries(field.configuration?.pinned_country_codes ?? [])
        setFirstNameLabelValue(firstNameLabel(field))
        setLastNameLabelValue(lastNameLabel(field))
        setStep('configure')
      } else {
        // Adding new field
        resetForm()
        setStep('select')
      }
    }
  }, [open, field])

  const resetForm = () => {
    setSelectedType(null)
    setLabel('')
    setAddImage(false)
    setHintImageFile(null)
    setHintImageList([])
    setExistingHintImageUrl(null)
    setRemoveHintImage(false)
    setAddInstructions(false)
    setHelpText('')
    setOptions([{ label: '', value: '', position: 0 }])
    setRemovedOptionIds([])
    setSessionOptions([{ agenda_session_id: 0, capacity: null, position: 0 }])
    setRemovedSessionOptionIds([])
    setShowBulkAdd(false)
    setBulkAddText('')
    setConsentStatement('')
    setMaxLength(200)
    setContentTypes(['image/jpeg', 'image/png', 'application/pdf'])
    setMaxSizeMb(10)
    setPinnedCountries([])
    setFirstNameLabelValue(DEFAULT_FIRST_NAME_LABEL)
    setLastNameLabelValue(DEFAULT_LAST_NAME_LABEL)
  }

  const handleSelectType = (type: string) => {
    setSelectedType(type)
    setStep('configure')
    setAddImage(false)
    setHintImageFile(null)
    setExistingHintImageUrl(null)
    setRemoveHintImage(false)

    // Set default max length based on type
    if (type === 'short_text') {
      setMaxLength(200)
    } else if (type === 'long_text') {
      setMaxLength(300)
    } else if (type === 'consent_text') {
      setMaxLength(20000)
    }
  }

  const handleAddOption = () => {
    setOptions([...options, { label: '', value: '', position: options.length }])
  }

  const handleBulkAddConfirm = () => {
    if (bulkAddLines.length === 0) return

    setOptions((prev) => {
      const baseOptions =
        prev.length === 1 && !prev[0].label && !prev[0].value && !prev[0].id ? [] : prev
      const startIndex = baseOptions.length
      const bulkOptions = bulkAddLines.map((line, idx) => ({
        label: line,
        value: line,
        position: startIndex + idx,
      }))
      return [...baseOptions, ...bulkOptions]
    })
    setBulkAddText('')
    setShowBulkAdd(false)
  }

  const handleRemoveOption = (index: number) => {
    const option = options[index]
    if (option?.id) {
      setRemovedOptionIds((prev) => [...prev, option.id!])
    }
    setOptions(options.filter((_, i) => i !== index))
  }

  const handleOptionChange = (index: number, field: 'label' | 'value', value: string) => {
    const newOptions = [...options]
    newOptions[index][field] = value
    setOptions(newOptions)
  }

  const handleAddSessionOption = () => {
    setSessionOptions([
      ...sessionOptions,
      { agenda_session_id: 0, capacity: null, position: sessionOptions.length },
    ])
  }

  const handleRemoveSessionOption = (index: number) => {
    const option = sessionOptions[index]
    if (option?.id) {
      setRemovedSessionOptionIds((prev) => [...prev, option.id!])
    }
    setSessionOptions(sessionOptions.filter((_, i) => i !== index))
  }

  const handleSessionOptionChange = (
    index: number,
    field: 'agenda_session_id' | 'capacity',
    value: number | null,
  ) => {
    const newOptions = [...sessionOptions]
    newOptions[index][field] = value as any
    setSessionOptions(newOptions)
  }
  const handleUnlimitedToggle = (index: number, checked: boolean) => {
    const newOptions = [...sessionOptions]
    newOptions[index].capacity = checked ? null : 1
    setSessionOptions(newOptions)
  }

  // The default Phone field and the Phone Number field both render a country
  // code picker, so both offer the pinned-country setting.
  const showCountryCodeSettings = field?.default_kind === 'phone' || selectedType === 'phone_number'
  // The mode itself is toggled from the field table; here the organiser only
  // names the two inputs the attendee will see.
  const showSplitNameLabels = isSplitName(field)

  const handleSubmit = async () => {
    if (!selectedType) return
    setSaving(true)

    const configuration: Record<string, unknown> = {}
    if (selectedType === 'short_text' || selectedType === 'long_text') {
      configuration.max_length = maxLength
    }
    if (selectedType === 'file_upload') {
      configuration.content_types = contentTypes
      configuration.max_size_mb = maxSizeMb
    }
    if (selectedType === 'consent_text') {
      configuration.consent_statement = consentStatement
    }
    if (showSplitNameLabels) {
      // name_format is deliberately not sent — it belongs to the table's toggle,
      // and the server merges this delta onto the stored configuration.
      configuration.first_name_label = firstNameLabelValue.trim() || DEFAULT_FIRST_NAME_LABEL
      configuration.last_name_label = lastNameLabelValue.trim() || DEFAULT_LAST_NAME_LABEL
    }

    const optionsAttributes =
      selectedType === 'multiple_choice' ||
      selectedType === 'dropdown' ||
      selectedType === 'single_choice'
        ? options.map((opt, idx) => ({
            id: opt.id,
            label: opt.label,
            value: opt.value,
            position: idx,
          }))
        : []

    const sessionOptionsAttributes =
      selectedType === 'session_choice'
        ? sessionOptions.map((opt, idx) => ({
            id: opt.id,
            agenda_session_id: opt.agenda_session_id,
            capacity: opt.capacity,
            position: idx,
          }))
        : []

    const formData = new FormData()
    formData.append('field[label]', label)
    formData.append('field[kind]', selectedType)
    formData.append('field[hint_text]', addInstructions ? helpText : '')
    formData.append('field[required]', String(field?.required ?? false))
    formData.append('field[editable]', String(field?.editable ?? false))
    formData.append('field[show_on_form]', String(field?.shown_on_form ?? true))
    formData.append('field[position]', String(field?.position ?? nextPosition))

    Object.entries(configuration).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (Array.isArray(value)) {
        if (value.length === 0) {
          // An emptied list still has to be sent. The server merges this
          // configuration onto what is stored, so a key that is simply absent
          // keeps its old value rather than being cleared — the same empty
          // sentinel pinned_country_codes uses below.
          formData.append(`field[configuration][${key}][]`, '')
        } else {
          value.forEach((v) => formData.append(`field[configuration][${key}][]`, String(v)))
        }
      } else {
        formData.append(`field[configuration][${key}]`, String(value))
      }
    })

    if (showCountryCodeSettings) {
      if (pinnedCountries.length === 0) {
        // Send an empty sentinel so the server knows to clear the existing list
        formData.append('field[configuration][pinned_country_codes][]', '')
      } else {
        pinnedCountries.forEach((name) => {
          formData.append('field[configuration][pinned_country_codes][]', name)
        })
      }
    }

    optionsAttributes.forEach((opt, idx) => {
      if (opt.id) formData.append(`field[options_attributes][${idx}][id]`, String(opt.id))
      formData.append(`field[options_attributes][${idx}][label]`, opt.label)
      formData.append(`field[options_attributes][${idx}][value]`, opt.value || opt.label)
      formData.append(`field[options_attributes][${idx}][position]`, String(opt.position ?? idx))
    })
    removedOptionIds.forEach((id, idx) => {
      formData.append(
        `field[options_attributes][${optionsAttributes.length + idx}][id]`,
        String(id),
      )
      formData.append(
        `field[options_attributes][${optionsAttributes.length + idx}][_destroy]`,
        'true',
      )
    })

    sessionOptionsAttributes.forEach((opt, idx) => {
      if (opt.id) formData.append(`field[session_options_attributes][${idx}][id]`, String(opt.id))
      formData.append(
        `field[session_options_attributes][${idx}][agenda_session_id]`,
        String(opt.agenda_session_id),
      )
      if (opt.capacity != null) {
        formData.append(`field[session_options_attributes][${idx}][capacity]`, String(opt.capacity))
      }
      formData.append(
        `field[session_options_attributes][${idx}][position]`,
        String(opt.position ?? idx),
      )
    })
    removedSessionOptionIds.forEach((id, idx) => {
      formData.append(
        `field[session_options_attributes][${sessionOptionsAttributes.length + idx}][id]`,
        String(id),
      )
      formData.append(
        `field[session_options_attributes][${sessionOptionsAttributes.length + idx}][_destroy]`,
        'true',
      )
    })

    if (hintImageFile) {
      formData.append('field[hint_image]', hintImageFile)
    }
    if (removeHintImage) {
      formData.append('field[remove_hint_image]', 'true')
    }

    try {
      if (field) {
        const response = await api.patch<{ field: RegistrationField }>(
          `/events/${eventSlug}/registration_form/fields/${field.id}`,
          formData,
        )
        if (response?.field) onSaved(response.field)
      } else {
        const response = await api.post<{ field: RegistrationField }>(
          `/events/${eventSlug}/registration_form/fields`,
          formData,
        )
        if (response?.field) onSaved(response.field)
      }
      resetForm()
      onClose()
    } catch (error: any) {
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to save field'
      message.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!field?.id) return
    const confirmed = window.confirm('Delete this form field? This cannot be undone.')
    if (!confirmed) return
    setSaving(true)
    try {
      await api.delete(`/events/${eventSlug}/registration_form/fields/${field.id}`)
      onDeleted(field.id)
      resetForm()
      onClose()
    } catch (error: any) {
      const apiErrors = error?.data?.errors || error?.errors
      const errorMessage =
        (Array.isArray(apiErrors) && apiErrors.join(', ')) ||
        error?.message ||
        'Failed to delete field'
      message.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const renderFieldTypeSelection = () => (
    <div className="space-y-2">
      {FIELD_TYPES.map((type) => (
        <div
          key={type.key}
          onClick={() => handleSelectType(type.key)}
          className="flex items-start gap-3 p-3 border border-gray-200 rounded hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
        >
          <div className="text-lg text-gray-600 mt-1">{type.icon}</div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">{type.label}</div>
            <div className="text-xs text-gray-500 mt-1">{type.description}</div>
          </div>
        </div>
      ))}
    </div>
  )

  const renderConfigurationForm = () => {
    return (
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedType === 'consent_text'
              ? 'Field name (for internal reference only, not shown to attendees)'
              : 'Type a question in the field below'}
          </label>
          <TextArea
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={selectedType === 'consent_text' ? 'e.g. Privacy Policy Consent' : 'Enter question'}
            rows={selectedType === 'consent_text' ? 2 : 4}
            size="small"
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-700">Add image to this question?</label>
            <Switch
              checked={addImage}
              onChange={(checked) => {
                setAddImage(checked)
                if (!checked) {
                  setHintImageFile(null)
                  if (existingHintImageUrl) setRemoveHintImage(true)
                } else {
                  setRemoveHintImage(false)
                }
              }}
            />
          </div>

          {addImage && (
            <div className="mt-2">
              <ImageUploadWithPreview
                label="Hint image"
                fileList={hintImageList}
                onChange={(list) => {
                  setHintImageList(list)
                  const fileObj = list[0]?.originFileObj || null
                  setHintImageFile(fileObj as File | null)
                  setRemoveHintImage(false)
                }}
                onRemove={() => {
                  setHintImageList([])
                  setHintImageFile(null)
                  setRemoveHintImage(true)
                }}
                previewUrl={!removeHintImage ? existingHintImageUrl : null}
                helpText="Supported: images/PDF. Max 2MB."
                accept="image/*,application/pdf"
                maxSizeMB={5}
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <label className="text-sm text-gray-700">Add text instructions to this question?</label>
            <Switch checked={addInstructions} onChange={setAddInstructions} />
          </div>

          {addInstructions && (
            <div className="mt-3">
              <TextArea
                value={helpText}
                onChange={(e) => setHelpText(e.target.value)}
                placeholder="Enter instructions"
                rows={3}
                maxLength={500}
                size="small"
              />
            </div>
          )}
        </div>

        {/* Field-specific configuration */}
        {(selectedType === 'multiple_choice' ||
          selectedType === 'dropdown' ||
          selectedType === 'single_choice') && (
          <div>
            <Divider />
            <div className="text-sm font-medium text-gray-700 mb-3">Options</div>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  {selectedType === 'dropdown' ? (
                    <Input
                      placeholder={`Option ${index + 1}`}
                      value={option.label}
                      onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                      size="small"
                    />
                  ) : (
                    <TextArea
                      placeholder={`Option ${index + 1}`}
                      value={option.label}
                      onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                      size="small"
                      autoSize={{ minRows: 1, maxRows: 4 }}
                    />
                  )}
                  {options.length > 1 && (
                    <Button
                      variant="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => handleRemoveOption(index)}
                    />
                  )}
                </div>
              ))}
              <Button
                variant="secondary"
                icon={<PlusCircleFilled />}
                onClick={handleAddOption}
                block
              >
                Add Option
              </Button>
              <div className="mt-2">
                <Button
                  icon={<PlusOutlined />}
                  variant="tertiary"
                  size="small"
                  onClick={() => setShowBulkAdd((prev) => !prev)}
                >
                  {showBulkAdd ? 'Hide bulk add' : 'Bulk add'}
                </Button>
                {showBulkAdd && (
                  <div>
                    <div className="text-sm text-gray-500 my-2">
                      Bulk add your choices by entering each one on a new line and click confirm to
                      save
                    </div>
                    <TextArea
                      value={bulkAddText}
                      onChange={(e) => setBulkAddText(e.target.value)}
                      placeholder="Enter each choice on a new line"
                      rows={4}
                      size="small"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Each line creates a separate option. To add a multi-line label, use the
                      individual option fields above.
                    </p>
                    <div className="flex justify-start mt-2">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={handleBulkAddConfirm}
                        disabled={bulkAddLines.length === 0}
                      >
                        Add Options
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {selectedType === 'session_choice' && (
          <div>
            <Divider />
            {sessionOptions.map((sessionOption, index) => (
              <div key={index} className="mb-4 p-4 border border-gray-200 rounded">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-sm">Session {index + 1}</span>
                  {sessionOptions.length > 1 && (
                    <MinusCircleOutlined
                      className="text-red-500 cursor-pointer"
                      onClick={() => handleRemoveSessionOption(index)}
                    />
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Session</label>
                    <Select
                      value={sessionOption.agenda_session_id || undefined}
                      onChange={(value) =>
                        handleSessionOptionChange(index, 'agenda_session_id', value)
                      }
                      placeholder="Select"
                      className="w-full"
                      options={agendaSessions.map((session) => ({
                        label: session.name,
                        value: session.id,
                      }))}
                      size="small"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Capacity</label>
                    <div className="flex items-center gap-2">
                      <InputNumber
                        value={sessionOption.capacity || undefined}
                        onChange={(value) => handleSessionOptionChange(index, 'capacity', value)}
                        placeholder="Enter capacity"
                        className="flex-1"
                        min={1}
                        size="small"
                      />
                      <Checkbox
                        size="small"
                        checked={sessionOption.capacity == null}
                        onChange={(e) => handleUnlimitedToggle(index, e.target.checked)}
                      >
                        Unlimited
                      </Checkbox>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="text"
              icon={<PlusCircleFilled />}
              onClick={handleAddSessionOption}
              block
            >
              Add Session
            </Button>
          </div>
        )}

        {selectedType === 'file_upload' && (
          <div>
            <Divider />
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed File Types
                </label>
                <Select
                  mode="multiple"
                  value={contentTypes}
                  onChange={setContentTypes}
                  className="w-full"
                  options={[
                    { label: 'JPEG Image', value: 'image/jpeg' },
                    { label: 'PNG Image', value: 'image/png' },
                    { label: 'PDF Document', value: 'application/pdf' },
                    { label: 'Word Document', value: 'application/msword' },
                    { label: 'Excel Spreadsheet', value: 'application/vnd.ms-excel' },
                  ]}
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max File Size (MB)
                </label>
                <InputNumber
                  value={maxSizeMb}
                  onChange={(value) => setMaxSizeMb(value || 10)}
                  min={1}
                  max={100}
                  className="w-full"
                  size="small"
                />
              </div>
            </div>
          </div>
        )}

        {selectedType === 'consent_text' && (
          <div>
            <Divider />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Consent Statement
              </label>
              <ReactQuill
                theme="snow"
                value={consentStatement}
                onChange={setConsentStatement}
                placeholder="Enter consent statement"
                modules={consentEditorModules}
                formats={consentEditorFormats}
              />
            </div>
          </div>
        )}

        {showSplitNameLabels && (
          <div>
            <Divider />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First name label
                </label>
                <Input
                  value={firstNameLabelValue}
                  onChange={(e) => setFirstNameLabelValue(e.target.value)}
                  placeholder={DEFAULT_FIRST_NAME_LABEL}
                  size="small"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last name label
                </label>
                <Input
                  value={lastNameLabelValue}
                  onChange={(e) => setLastNameLabelValue(e.target.value)}
                  placeholder={DEFAULT_LAST_NAME_LABEL}
                  size="small"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This field is collected as two inputs. The name above is what the two of them are
              filed under everywhere else — badges, exports and merge tags still read one full name.
            </p>
          </div>
        )}

        {showCountryCodeSettings && (
          <div>
            <Divider />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pinned countries (top of mobile dropdown)
              </label>
              <Select
                mode="multiple"
                value={pinnedCountries}
                onChange={setPinnedCountries}
                placeholder="Search and select countries..."
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                }
                options={countryOptions.map((c) => ({
                  label: c.dial_code ? `${c.name} (+${c.dial_code})` : c.name,
                  value: c.iso,
                }))}
                size="small"
              />
              <p className="text-xs text-gray-500 mt-1">
                Selected countries appear at the top of the country code picker on the registration
                form.
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }

  const getTitle = () => {
    if (step === 'select') {
      return 'Add New Field'
    }
    const selectedFieldType = FIELD_TYPES.find((t) => t.key === selectedType)
    return selectedFieldType?.label || 'Add New Field'
  }

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          {/* {(step === 'configure' || field) && (
            <ArrowLeftOutlined
              onClick={handleBack}
              className="cursor-pointer text-gray-600 hover:text-gray-900"
            />
          )} */}
          <span>{getTitle()}</span>
        </div>
      }
      onClose={onClose}
      open={open}
      showFooter={step === 'configure'}
      onSubmit={handleSubmit}
      submitText={field ? 'Update' : 'Add'}
      loading={saving}
      customFooter={
        step === 'configure' ? (
          <div className="flex justify-between w-full">
            {field ? (
              <Button variant="text" danger onClick={handleDelete} disabled={saving}>
                Delete Field
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-3">
              <Button variant="secondary" size="small" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" size="small" onClick={handleSubmit} loading={saving}>
                {field ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        ) : undefined
      }
    >
      {step === 'select' ? renderFieldTypeSelection() : renderConfigurationForm()}
    </Drawer>
  )
}
